import { getCookieValue } from '@/utils/get-cookie-value';
import { setCookieValue } from '@/utils/set-cookie-value';

import { SEGMENT_ANONYMOUS_ID_PARAM } from './segment-anonymous-id';

const COOKIE_NAME = '__extern_vid';
// 2 years — long-lived identity, not the attribution click-id TTL. Matches
// the lifetime of typical first-party analytics identity cookies (e.g. _ga).
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365 * 2;

/** In-session id when the cookie can't be read or written (private mode, disabled cookies). */
let memoryFallbackId: string | undefined;

function generateId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * The id `extern-website` forwards on the sign-up hand-off, on the page loads that carry one.
 *
 * Reading it here is what keeps one visitor from becoming two. The Segment snippet adopts this
 * parameter no matter what; if the cookie kept its own value beside it, the signup provision and
 * every auth event would go out under one id while Segment's events used another, and the NEXT
 * page load — parameter gone — would push the cookie's id back into Segment and flip its identity
 * mid-funnel.
 */
function readForwardedVisitorId(): string | undefined {
  try {
    return new URLSearchParams(window.location.search).get(SEGMENT_ANONYMOUS_ID_PARAM) || undefined;
  } catch {
    // A malformed query string must not be able to take the id down.
    return;
  }
}

/**
 * Writes the id and reports whether the browser kept it. A dropped write (cookies disabled, quota)
 * falls back to memory so the id at least stays stable for this session.
 */
function persistVisitorId(id: string): string {
  try {
    setCookieValue(COOKIE_NAME, id, COOKIE_MAX_AGE_SECONDS);
    if (getCookieValue(COOKIE_NAME) !== id) {
      memoryFallbackId = id;
    }
  } catch {
    memoryFallbackId = id;
  }

  return id;
}

/**
 * Own first-party visitor id — generated and read without Segment, PostHog or
 * GTM present. Those vendors are meant to inherit this value (e.g. Segment's
 * `analytics.setAnonymousId()`) rather than being its source, so a visitor
 * who rejects consent (and never loads those vendors) keeps the same id as
 * one who accepts. Consumed by `getAnonymousCorrelationId`.
 *
 * Cookie-backed and scoped to `.extern.com` (see `setCookieValue`) so the
 * marketing site and the app share one id. Falls back to an in-memory id for
 * the current session when cookies are blocked; never throws.
 */
export function getFirstPartyVisitorId(): string {
  if (typeof window === 'undefined') {
    if (!memoryFallbackId) {
      memoryFallbackId = generateId();
    }

    return memoryFallbackId;
  }

  // Outranks the stored cookie: a hand-off means this browser is continuing a journey that began
  // on another origin, so that origin's id is the one every vendor and our own backend must use.
  // Overwriting is safe precisely here — the parameter only ever arrives from `extern-website`,
  // carrying an account that was just created, which is the same reason the snippet resets before
  // adopting it.
  const forwardedId = readForwardedVisitorId();

  if (forwardedId) {
    return persistVisitorId(forwardedId);
  }

  try {
    const existing = getCookieValue(COOKIE_NAME);
    if (existing) {
      return existing;
    }
  } catch {
    // document.cookie can throw in some restricted/sandboxed contexts
  }

  return persistVisitorId(memoryFallbackId ?? generateId());
}
